import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import {
  fetchDashboardActiveJobs,
  fetchPartnerDashboardActiveJobs,
  fetchPlatformDashboardActiveJobs,
  cancelDashboardJob,
  cancelPartnerDashboardJob,
  cancelPlatformDashboardJob,
} from "../api/client";
import {
  buildLocalDashboardJob,
  mergeDashboardJobRecords,
  refreshDashboardJobProgress,
} from "../utils/dashboardJobLabels";
import { buildDashboardScopeKeyFromJob } from "../utils/dashboardScopeKey";
import { pollDashboardJob, pollPartnerDashboardJob, pollPlatformDashboardJob } from "../hooks/dashboardDeferredPoll";

const DashboardJobsContext = React.createContext({
  jobs: [],
  registerJob: () => {},
  cancelJob: async () => {},
  cancellingJobId: null,
  isJobCancelled: () => false,
  isScopeCancelled: () => false,
  hasActiveBackgroundJobs: false,
});

export function useDashboardBackgroundJobs() {
  return useContext(DashboardJobsContext);
}

export function useDashboardJobsRegister() {
  const { registerJob } = useContext(DashboardJobsContext);
  return registerJob;
}

function useNowTick(enabled, intervalMs = 1000) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!enabled) return undefined;
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [enabled, intervalMs]);
  return now;
}

export function DashboardJobsProvider({ children, enabled = true, variant = "workspace" }) {
  const queryClient = useQueryClient();
  const [localJobs, setLocalJobs] = useState({});
  const [pollIntervalMs, setPollIntervalMs] = useState(false);
  const [cancellingJobId, setCancellingJobId] = useState(null);
  const [cancelledJobIds, setCancelledJobIds] = useState(() => new Set());
  const [cancelledScopeKeys, setCancelledScopeKeys] = useState(() => new Set());

  const pollingJobIdsRef = useRef(new Set());
  const completedPollJobIdsRef = useRef(new Set());
  const pollAbortControllersRef = useRef(new Map());
  const cancelledJobIdsRef = useRef(cancelledJobIds);
  const cancelledScopeKeysRef = useRef(cancelledScopeKeys);
  const mergedJobsRef = useRef([]);
  const localJobsRef = useRef({});

  cancelledJobIdsRef.current = cancelledJobIds;
  cancelledScopeKeysRef.current = cancelledScopeKeys;

  const isPartner = variant === "partner";
  const isPlatform = variant === "platform";
  const activeJobsQueryKey = isPlatform
    ? ["platform-dashboard-active-jobs"]
    : isPartner
      ? ["partner-dashboard-active-jobs"]
      : ["dashboard-active-jobs"];
  const fetchActiveJobs = isPlatform
    ? fetchPlatformDashboardActiveJobs
    : isPartner
      ? fetchPartnerDashboardActiveJobs
      : fetchDashboardActiveJobs;
  const cancelJobRequest = isPlatform
    ? cancelPlatformDashboardJob
    : isPartner
      ? cancelPartnerDashboardJob
      : cancelDashboardJob;
  const dashboardQueryPrefix = isPlatform
    ? "platform-dashboard"
    : isPartner
      ? "partner-dashboard"
      : "dashboard";
  const pollJobFn = isPlatform ? pollPlatformDashboardJob : isPartner ? pollPartnerDashboardJob : pollDashboardJob;

  const isJobCancelled = useCallback((jobId) => cancelledJobIds.has(String(jobId || "")), [cancelledJobIds]);
  const isScopeCancelled = useCallback(
    (scopeKey) => cancelledScopeKeys.has(String(scopeKey || "")),
    [cancelledScopeKeys]
  );

  const markCancelled = useCallback((jobId, scopeKey) => {
    const safeId = String(jobId || "").trim();
    if (safeId) {
      setCancelledJobIds((prev) => new Set(prev).add(safeId));
      pollingJobIdsRef.current.delete(safeId);
      completedPollJobIdsRef.current.add(safeId);
      const controller = pollAbortControllersRef.current.get(safeId);
      if (controller) {
        controller.abort();
        pollAbortControllersRef.current.delete(safeId);
      }
    }
    const safeScope = String(scopeKey || "").trim();
    if (safeScope) {
      setCancelledScopeKeys((prev) => new Set(prev).add(safeScope));
    }
  }, []);

  const clearDeferredQueriesForJob = useCallback(
    (jobId, scopeKey) => {
      queryClient.setQueriesData(
        {
          predicate: (query) => {
            const key = query.queryKey?.[0];
            return (
              key === dashboardQueryPrefix ||
              key === `${dashboardQueryPrefix}-preview` ||
              key === "survey-analytics" ||
              key === "partner-survey-analytics"
            );
          },
        },
        (old) => {
          if (!old) return old;
          const matchesJob = String(old?.jobMeta?.jobId) === String(jobId);
          const matchesScope = scopeKey && String(old?.scopeKey || "") === String(scopeKey);
          if (!matchesJob && !matchesScope) return old;
          return {
            ...old,
            data: null,
            deferred: false,
            jobMeta: null,
            userCancelled: true,
            scopeKey,
          };
        }
      );
    },
    [queryClient, dashboardQueryPrefix]
  );

  const { data } = useQuery(activeJobsQueryKey, fetchActiveJobs, {
    enabled: Boolean(enabled),
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchInterval: pollIntervalMs,
  });

  const registerJob = useCallback(
    (meta, extras = {}) => {
      if (!meta?.jobId) return;
      const jobId = String(meta.jobId);
      if (cancelledJobIdsRef.current.has(jobId)) return;

      setLocalJobs((prev) => {
        const existing = prev[jobId];
        const built = buildLocalDashboardJob(meta, extras);
        if (existing?.createdAt) {
          built.createdAt = existing.createdAt;
          built._maxElapsedMs = existing._maxElapsedMs || 0;
        }
        return { ...prev, [jobId]: built };
      });
      setPollIntervalMs(3000);
    },
    []
  );

  const cancelJob = useCallback(
    async (jobId) => {
      const safeId = String(jobId || "").trim();
      if (!safeId || cancelledJobIdsRef.current.has(safeId)) return;

      const job =
        mergedJobsRef.current.find((j) => String(j.jobId) === safeId) ||
        localJobsRef.current[safeId];
      const scopeKey = buildDashboardScopeKeyFromJob(job);

      markCancelled(safeId, scopeKey);
      clearDeferredQueriesForJob(safeId, scopeKey);
      setCancellingJobId(safeId);

      setLocalJobs((prev) => {
        if (!prev[safeId]) return prev;
        const next = { ...prev };
        delete next[safeId];
        return next;
      });

      queryClient.setQueryData(activeJobsQueryKey, (old) => {
        const jobs = Array.isArray(old?.jobs) ? old.jobs : [];
        const nextJobs = jobs.filter((j) => String(j.jobId) !== safeId);
        if (nextJobs.length === jobs.length) return old;
        return { ...old, jobs: nextJobs };
      });

      try {
        await cancelJobRequest(safeId);
      } catch (e) {
        console.warn("cancelDashboardJob:", e?.message || e);
      } finally {
        setCancellingJobId((current) => (current === safeId ? null : current));
        if (!mergedJobsRef.current.length) {
          setPollIntervalMs(false);
        }
      }
    },
    [activeJobsQueryKey, cancelJobRequest, clearDeferredQueriesForJob, markCancelled, queryClient]
  );

  const mergedJobs = useMemo(() => {
    const map = new Map();
    for (const job of data?.jobs || []) {
      if (!job?.jobId) continue;
      const jobId = String(job.jobId);
      if (cancelledJobIds.has(jobId)) continue;
      const localJob = localJobs[jobId];
      map.set(jobId, localJob ? mergeDashboardJobRecords(job, localJob) : job);
    }
    for (const [jobId, localJob] of Object.entries(localJobs)) {
      if (cancelledJobIds.has(jobId)) continue;
      if (!map.has(jobId)) map.set(jobId, localJob);
    }
    return [...map.values()].sort(
      (a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
    );
  }, [data?.jobs, localJobs, cancelledJobIds]);

  mergedJobsRef.current = mergedJobs;
  localJobsRef.current = localJobs;

  useEffect(() => {
    if (data === undefined) return;
    setLocalJobs((prev) => {
      const next = { ...prev };
      let changed = false;
      const apiIds = new Set((data.jobs || []).map((j) => String(j.jobId)));

      for (const job of data.jobs || []) {
        if (next[job.jobId]) {
          delete next[job.jobId];
          changed = true;
        }
      }

      for (const [id, job] of Object.entries(next)) {
        if (cancelledJobIdsRef.current.has(id)) {
          delete next[id];
          changed = true;
          continue;
        }
        if (!apiIds.has(id)) {
          const age = Date.now() - new Date(job.createdAt || 0).getTime();
          if (age > 20_000) {
            delete next[id];
            changed = true;
          }
        }
      }

      return changed ? next : prev;
    });
  }, [data]);

  useEffect(() => {
    const count = mergedJobs.length;
    if (!count) {
      setPollIntervalMs(false);
      return;
    }
    setPollIntervalMs(data?.pollAfterMs || 3000);
  }, [mergedJobs.length, data?.pollAfterMs]);

  useEffect(() => {
    for (const job of mergedJobs) {
      const jobId = String(job.jobId);
      if (!jobId) continue;
      if (cancelledJobIdsRef.current.has(jobId)) continue;
      if (pollingJobIdsRef.current.has(jobId)) continue;
      if (completedPollJobIdsRef.current.has(jobId)) continue;
      if (job.status === "cancel_requested") continue;

      pollingJobIdsRef.current.add(jobId);
      const controller = new AbortController();
      pollAbortControllersRef.current.set(jobId, controller);
      const scopeKey = buildDashboardScopeKeyFromJob(job);

      void (async () => {
        try {
          await pollJobFn(jobId, job.pollAfterMs || 3000, 600_000, { signal: controller.signal });
          if (cancelledJobIdsRef.current.has(jobId)) return;
          completedPollJobIdsRef.current.add(jobId);
          queryClient.invalidateQueries([dashboardQueryPrefix]);
          queryClient.invalidateQueries([`${dashboardQueryPrefix}-preview`]);
          queryClient.invalidateQueries(["survey-analytics"]);
          queryClient.invalidateQueries(["survey-analytics-preview"]);
          queryClient.invalidateQueries(["partner-survey-analytics"]);
          queryClient.invalidateQueries(["partner-survey-analytics-preview"]);
        } catch (e) {
          if (e?.cancelled || cancelledJobIdsRef.current.has(jobId)) {
            markCancelled(jobId, scopeKey);
            clearDeferredQueriesForJob(jobId, scopeKey);
          }
        } finally {
          pollingJobIdsRef.current.delete(jobId);
          pollAbortControllersRef.current.delete(jobId);
        }
      })();
    }
  }, [
    mergedJobs,
    pollJobFn,
    queryClient,
    dashboardQueryPrefix,
    markCancelled,
    clearDeferredQueriesForJob,
  ]);

  useEffect(() => {
    return () => {
      for (const controller of pollAbortControllersRef.current.values()) {
        controller.abort();
      }
      pollAbortControllersRef.current.clear();
      pollingJobIdsRef.current.clear();
    };
  }, []);

  const now = useNowTick(mergedJobs.length > 0);
  const jobsWithProgress = useMemo(
    () => mergedJobs.map((job) => refreshDashboardJobProgress(job, now)),
    [mergedJobs, now]
  );

  const value = useMemo(
    () => ({
      jobs: jobsWithProgress,
      registerJob,
      cancelJob,
      cancellingJobId,
      isJobCancelled,
      isScopeCancelled,
      hasActiveBackgroundJobs: jobsWithProgress.length > 0,
    }),
    [
      jobsWithProgress,
      registerJob,
      cancelJob,
      cancellingJobId,
      isJobCancelled,
      isScopeCancelled,
    ]
  );

  return (
    <DashboardJobsContext.Provider value={value}>{children}</DashboardJobsContext.Provider>
  );
}
